// eslint-disable-next-line import/no-cycle
import {
  getPositionLiqPrice,
  getUpdatedAccountOnFill,
} from '@/features/account/utils/math'; // eslint-disable-line import/no-restricted-paths
import { FEE_DECIMALS } from '@/features/markets/api/get-exchange-info'; // eslint-disable-line import/no-restricted-paths
// eslint-disable-next-line import/no-cycle
import { getInitMarginRatio } from '@/features/markets/utils/helpers'; // eslint-disable-line import/no-restricted-paths
import { getIsStatusEval } from '@/features/vest-capital/utils/status'; // eslint-disable-line import/no-restricted-paths
import { Account, Position, useAccountStore } from '@/store/use-account-store'; // eslint-disable-line import/no-cycle
import {
  COLLATERAL_DECIMALS,
  getMarketStore,
  OrderBook,
} from '@/store/use-markets-store';
import {
  adjustDecimals,
  BigDecimal,
  bigIntToDecimalStr,
} from '@/utils/value-format';

// eslint-disable-next-line import/no-cycle
import { OrderFormSchema } from '../components';

import { validateLimitPrice } from './helpers';

export const getMaxFillSize = (
  isBuy: boolean,
  book: OrderBook,
  sizeDecimals: bigint,
): BigDecimal => {
  const levels = isBuy ? book.asks : book.bids;
  const maxFillSize = levels.reduce((acc, [_price, qty]) => acc + qty, 0n);
  return {
    bigint: maxFillSize,
    decimal: bigIntToDecimalStr(maxFillSize, sizeDecimals),
  };
};

export const getEstFillPrice = (
  isBuy: boolean,
  size: bigint,
  book: OrderBook,
  priceDecimals: bigint,
): (BigDecimal & { enoughLiquidity: boolean }) | undefined => {
  if (size === 0n) return;

  const levels = isBuy ? book.asks : book.bids;

  if (!levels || levels.length === 0) {
    return;
  }

  let remaining = size;
  let cost = 0n;
  let consumed = 0n;
  let usedExtrapolation = false;

  for (let i = 0; i < levels.length && remaining > 0n; i += 1) {
    const [price, qty] = levels[i];

    if (qty <= 0n) {
      continue;
    }

    const take = qty >= remaining ? remaining : qty;

    consumed += take;
    remaining -= take;
    cost += price * take;
  }

  if (remaining > 0n) {
    if (levels.length < 2) {
      const priceBigInt = consumed === 0n ? 0n : cost / consumed;

      return {
        bigint: priceBigInt,
        decimal: bigIntToDecimalStr(priceBigInt, priceDecimals),
        enoughLiquidity: false,
      };
    }

    const [lastPrice, lastQty] = levels[levels.length - 1];
    const [prevPrice, prevQty] = levels[levels.length - 2];

    if (lastQty <= 0n || prevQty <= 0n) {
      const priceBigInt = consumed === 0n ? 0n : cost / consumed;

      return {
        bigint: priceBigInt,
        decimal: bigIntToDecimalStr(priceBigInt, priceDecimals),
        enoughLiquidity: false,
      };
    }

    const direction = isBuy ? 1n : -1n;
    const marginalPriceDelta = (direction * (lastPrice - prevPrice)) / lastQty;
    const estPrice = lastPrice + direction * marginalPriceDelta * remaining;
    const avgPrice = (lastPrice + estPrice) / 2n;

    cost += avgPrice * remaining;
    consumed += remaining;
    remaining = 0n;
    usedExtrapolation = true;
  }

  if (consumed === 0n) {
    return {
      bigint: 0n,
      decimal: bigIntToDecimalStr(0n, priceDecimals),
      enoughLiquidity: false,
    };
  }

  const priceBigInt = cost / consumed;

  return {
    bigint: priceBigInt,
    decimal: bigIntToDecimalStr(priceBigInt, priceDecimals),
    enoughLiquidity: remaining === 0n && !usedExtrapolation,
  };
};

export const getFee = (
  symbol: string,
  size: bigint,
  price: bigint,
  options?: {
    address?: string;
  },
) => {
  const { marketSpec } = getMarketStore(symbol).getState();
  const capitalStatus = useAccountStore
    .getState()
    .getCapitalStatus(options?.address ?? useAccountStore.getState().address);
  const isEval = getIsStatusEval(capitalStatus);

  if (isEval || size === 0n || !marketSpec.takerFee) return 0n;

  const fee = adjustDecimals(
    price * marketSpec.takerFee * size,
    COLLATERAL_DECIMALS -
      marketSpec.priceDecimals -
      marketSpec.sizeDecimals -
      FEE_DECIMALS,
  );

  return fee;
};

export const getSlippage = (
  isBuy: boolean,
  openPrice: bigint,
  book: OrderBook,
): BigDecimal => {
  if (!book?.asks.length || !book?.bids.length) {
    return {
      bigint: 0n,
      decimal: bigIntToDecimalStr(0n, 4n),
    };
  }

  const midPrice = (book.asks[0][0] + book.bids[0][0]) / 2n;
  const slippage =
    ((isBuy ? 1n : -1n) * (openPrice - midPrice) * 10n ** 6n) / midPrice;

  return {
    bigint: slippage,
    decimal: bigIntToDecimalStr(slippage, 4n),
  };
};

export const getOrderValue = (
  symbol: string,
  size: bigint,
  price: bigint,
): BigDecimal => {
  const { marketSpec } = getMarketStore(symbol).getState();
  if (size === 0n)
    return {
      bigint: 0n,
      decimal: bigIntToDecimalStr(
        0n,
        marketSpec.sizeDecimals + marketSpec.priceDecimals,
      ),
    };
  const orderValue = size * price;
  return {
    bigint: orderValue,
    decimal: bigIntToDecimalStr(
      orderValue,
      marketSpec.sizeDecimals + marketSpec.priceDecimals,
    ),
  };
};

export const getOrderValueFromPrice = (
  symbol: string,
  size: bigint,
  price: bigint,
): BigDecimal | undefined => {
  if (size === 0n) return;
  const orderValue = size * price;
  const { marketSpec } = getMarketStore(symbol).getState();
  return {
    bigint: orderValue,
    decimal: bigIntToDecimalStr(
      orderValue,
      marketSpec.sizeDecimals + marketSpec.priceDecimals,
    ),
  };
};

export const getLimitPriceFromMaxSlippage = (
  isBuy: boolean,
  maxSlippagePct: bigint, // pcts should always be 4 decimal precision
  markPrice: bigint,
  priceDecimals: bigint,
): BigDecimal => {
  const limitPrice =
    ((10n ** 4n + (isBuy ? maxSlippagePct : -maxSlippagePct)) * markPrice) /
    10n ** 4n;
  return {
    bigint: limitPrice,
    decimal: bigIntToDecimalStr(limitPrice, priceDecimals),
  };
};

export const getMaxLeverage = (
  initMarginRatio: bigint,
): BigDecimal | undefined => {
  if (initMarginRatio === 0n) return undefined;
  const maxLeverage = initMarginRatio
    ? BigInt((10n ** 6n / initMarginRatio + 99n) / 100n) * 100n
    : 0n;
  return {
    bigint: maxLeverage,
    decimal: bigIntToDecimalStr(maxLeverage, 2n),
  };
};

// returns sizeDecimals
export const getSizeFromNumeraire = (
  symbol: string,
  numeraireSize: bigint, // expects in collateral decimals
  price: bigint,
  roundUp: boolean = true,
): bigint => {
  if (numeraireSize === 0n || price === 0n) return 0n;
  const { sizeDecimals, priceDecimals } =
    getMarketStore(symbol).getState().marketSpec;
  const decimalsPadding = COLLATERAL_DECIMALS + 1n;
  const bigSize = adjustDecimals(
    (numeraireSize * 10n ** decimalsPadding) / price,
    sizeDecimals - (COLLATERAL_DECIMALS - priceDecimals),
  ); // sizeDecimals + decimalsPadding
  const size = adjustDecimals(bigSize, -decimalsPadding);
  if (roundUp) {
    return bigSize > 0n && size == 0n ? 1n : size; // round up
  } else {
    return size;
  }
};

// TODO: limit prices
export const getEstLiqPrice = (
  symbol: string,
  isBuy: boolean,
  size: bigint,
  account: Account,
  orderInitMarginRatio?: bigint,
  price?: bigint,
): BigDecimal | undefined => {
  if (size === 0n) return;
  const { marketData } = getMarketStore(symbol).getState();
  const updatedAccount = getUpdatedAccountOnFill(
    symbol,
    isBuy,
    size,
    price ?? marketData.markPrice,
    0n,
    orderInitMarginRatio ?? getInitMarginRatio(symbol),
    account,
  );
  return getPositionLiqPrice(symbol, updatedAccount);
};

// Assumes always reducing position
export const getPriceFromPnl = (
  symbol: string,
  pnl: bigint, // in collateral decimals
  size: bigint,
  pos: Pick<Position, 'cost' | 'isLong' | 'size'>,
): BigDecimal => {
  const cost = size === pos.size ? pos.cost : (pos.cost * size) / pos.size;
  const signedCost = (pos.isLong ? 1n : -1n) * cost;
  const signedSize = (pos.isLong ? 1n : -1n) * size;
  const { marketSpec } = getMarketStore(symbol).getState();
  const price =
    (adjustDecimals(
      pnl,
      marketSpec.sizeDecimals + marketSpec.priceDecimals - COLLATERAL_DECIMALS,
    ) +
      signedCost) /
    signedSize;
  return {
    bigint: price,
    decimal: bigIntToDecimalStr(price, marketSpec.priceDecimals),
  };
};

export const generateScaleOrders = (
  rawOrder: OrderFormSchema,
  sizeString: string,
): OrderFormSchema[] => {
  const {
    startPrice: startPriceString,
    endPrice: endPriceString,
    ordersCount: ordersCountString,
    sizeSkew: sizeSkewString,
  } = rawOrder;

  const size = Number(sizeString);
  const startPrice = Number(startPriceString);
  const endPrice = Number(endPriceString);
  const ordersCount = Number(ordersCountString);
  const sizeSkew = Number(sizeSkewString);

  if (
    !(
      size > 0 &&
      startPrice > 0 &&
      endPrice > 0 &&
      ordersCount > 0 &&
      sizeSkew > 0
    )
  ) {
    return [];
  }

  const prices: string[] = [];
  const priceStep: number =
    ordersCount > 1
      ? (endPrice - startPrice) / (ordersCount - 1)
      : endPrice - startPrice;
  const weights: number[] = [];
  let weightSum: number = 0;

  for (let i = 0; i < ordersCount; i++) {
    const limitPrice = validateLimitPrice(
      rawOrder.order.symbol,
      (startPrice + priceStep * i).toString(),
    );
    prices.push(limitPrice);
    const weight =
      ordersCount > 1 ? Math.pow(sizeSkew, i / (ordersCount - 1)) : 1;

    weightSum += weight;
    weights.push(weight);
  }

  const normalizedWeights = weights.map((w) => w / (weightSum || 1));

  return normalizedWeights.map((weight, index) => ({
    ...rawOrder,
    order: {
      ...rawOrder.order,
      size: (size * weight).toString(),
      limitPrice: prices[index],
    },
  }));
};
